import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGET_learningStyleQuestionsQuery } from "redux/api/studentsApi"
import { STATUS_COLORS } from "utils/customizedTheme"
import { useGET_kpiEvaluationsQuery } from "redux/api/kpiEvaluationsApi"
import { OnlinePrediction } from "@mui/icons-material"
import { useGET_setupOptionsQuery } from "redux/api/setupApi"


interface IProps {
  benchmark: string
  student: string
  studentData: any
  benchmarkData: any
}

const BenchmarkStudentDiagnostic: FC<IProps> = ({benchmark, student, benchmarkData, studentData }) => {
  const { t } = useTranslation()
  
  const { data: setupData } = useGET_setupOptionsQuery()
  
  const {
    data: kpiData,
  } = useGET_kpiEvaluationsQuery({assessment: benchmarkData?.assessment?.id, page: 1}, {skip: !benchmarkData})
    
  const kpi = kpiData?.results?.length > 0 ? kpiData.results[0] : null

  const {
    data: learningStyle,
  } = useGET_learningStyleQuestionsQuery(student, {skip: !student})
    
  const learningCategories = learningStyle?.categories?.map((category) => {
    return {
      name: category.title,
      value: category.percentage,
      fill: category.color_hex_code,
    }
  })

  const deviation = studentData ? studentData.baseline_score - 50 : 0

  if(!studentData || !benchmarkData || !setupData) return <></>

  return (
    <>
        <div className="info-box">
            <div className="info-box-title" style={{ textAlign: 'left' }}>
                <div className="details-row">
                    <div style={{ flex: 2 }}>{benchmarkData.benchmark.title}</div>
                    <div>
                        {benchmarkData.latest_benchmark_evaluation && <span>{benchmarkData.latest_benchmark_evaluation.score}%</span>}
                    </div>
                </div>
            </div>

            {setupData.filter(sd => sd.slug == "kpi_tracker_zpd_status" && sd.is_enabled).length == 1 && 
            <div className="info-box-content">
                <div className="stats-bar">
                    <div className="stats-bar-fill" style={{ 
                         width: `${benchmarkData.latest_benchmark_evaluation.score}%`,
                         background: STATUS_COLORS[benchmarkData.latest_benchmark_evaluation.status]
                    }}></div>

                    <div className="stats-bar-line" style={{ left: '50%' }}></div>
                    <div className="stats-bar-line" style={{ left: '80%' }}></div>
                </div>
                <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: '5px' }}>
                    <div style={{ width: '50% '}}>{t('zpd-tags.gap')}</div>
                    <div style={{ width: '30%' }}>{t('zpd-tags.bridge')}</div>
                    <div>{t('zpd-tags.strength')}</div>
                </div>
            </div>}
        </div>

        <div className="info-row">

                    {kpi && setupData.filter(sd => sd.slug == "benchmark_tracker_what" && sd.is_enabled).length == 1 && <div className="info-box">
                        <div className="info-box-title">{t('common.benchmark-what')}</div>
                        <div className="info-box-content">

                            <div className="stats-rows">
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.mastery')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${benchmarkData.latest_benchmark_evaluation.score}%`,
                                            background: '#2136d4ff'
                                    }}></div>
                                        <div className="stats-bar-value">{benchmarkData.latest_benchmark_evaluation.score}%</div>
                                    </div>
                                </div>
                                
                                {setupData.filter(sd => sd.slug == "kpi_tracker_accuracy_rate" && sd.is_enabled).length == 1 && 
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.accuracy-rate')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpi.accuracy_rate}%`,
                                            background: '#b82aceff'
                                    }}></div>
                                        <div className="stats-bar-value">{kpi.accuracy_rate}%</div>
                                    </div>
                                </div>}

                                {setupData.filter(sd => sd.slug == "kpi_tracker_time_on_task" && sd.is_enabled).length == 1 &&         
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.time-on-task')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpi.time_on_task}%`,
                                            background: 'orange'
                                    }}></div>
                                        <div className="stats-bar-value">{kpi.time_on_task}%</div>
                                    </div>
                                </div>}

                                {setupData.filter(sd => sd.slug == "kpi_tracker_growth_trend" && sd.is_enabled).length == 1 && 
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.growth-trend')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpi.growth_trend > 0 ? kpi.growth_trend : (-1) * kpi.growth_trend}%`,
                                            background: kpi.growth_trend > 0 ? 'rgba(8, 218, 33, 1)' : 'red'
                                    }}></div>
                                        <div className="stats-bar-value">{kpi.growth_trend}%</div>
                                    </div>
                                </div>}

                            </div>
                           
                        </div>
                    </div>}

                    {learningCategories?.length > 0 && 
                        setupData.filter(sd => sd.slug == "benchmark_tracker_how" && sd.is_enabled).length == 1  &&
                    <div className="info-box">
                        <div className="info-box-title">{t('common.benchmark-how')}</div>
                        <div className="info-box-content">
                            <div className="stats-rows">
                                {learningCategories.map(c => <div key={c.name} className="stats-row">
                                    <div className="stats-row-label">{c.name}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${c.value}%`,
                                            background: c.fill
                                        }}></div>
                                        <div className="stats-bar-value">{c.value}%</div>
                                    </div>
                                </div>)}
                            </div>
                        </div>
                    </div>}
        
                </div>


                <div className="info-row">

                    {setupData.filter(sd => sd.slug == "benchmark_tracker_why" && sd.is_enabled).length == 1 &&
                    <div className="info-box">
                        <div className="info-box-title">{t('common.benchmark-why')}</div>
                        <div className="info-box-content">
                            <div className="stats-rows">
                                {kpi && <div className="stats-row">
                                    <div className="stats-row-label">{t('common.zpd-anchor')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpi.accuracy_rate}%`,
                                            background: '#f1b4d3ff'
                                    }}></div>
                                        <div className="stats-bar-label" style={{ textTransform: 'uppercase' }}>{kpi.status}</div>
                                        <div className="stats-bar-value">{kpi.accuracy_rate}%</div>
                                    </div>
                                </div>}

                                {benchmarkData.latest_benchmark_evaluation && <div className="stats-row">
                                    <div className="stats-row-label">{t('common.zpd-tag')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${benchmarkData.latest_benchmark_evaluation.score}%`,
                                            background: '#a2b1f2ff'
                                    }}></div>
                                        <div className="stats-bar-label" style={{ textTransform: 'uppercase' }}>{benchmarkData.latest_benchmark_evaluation.status}</div>
                                        <div className="stats-bar-value">{benchmarkData.latest_benchmark_evaluation.score}%</div>
                                    </div>
                                </div>}

                                {kpi && <div className="stats-row">
                                    <div className="stats-row-label">{t('common.performance')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpi.accuracy_rate}%`,
                                            background: '#83e272ff'
                                    }}></div>
                                        <div className="stats-bar-value">{kpi.accuracy_rate}%</div>
                                    </div>
                                </div>}

                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.bgl-deviation')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${deviation > 0 ? deviation : -1*deviation }%`,
                                            background: '#65f1c5ff'
                                    }}></div>
                                        <div className="stats-bar-value">{deviation}%</div>
                                    </div>
                                </div>

                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.risk')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${studentData.risk}%`,
                                            background: 'orange'
                                    }}></div>
                                        <div className="stats-bar-value">{studentData.risk}%</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>}

                    {kpi && setupData.filter(sd => sd.slug == "benchmark_tracker_next" && sd.is_enabled).length == 1 && 
                    <div className="info-box">
                        <div className="info-box-title">{t('common.benchmark-next')}</div>
                        <div className="info-box-content">
                            <div><OnlinePrediction sx={{ fontSize: 50, color: 'red', float: 'left', marginRight: '10px' }} /> 
                                <span style={{ fontSize: '18px', fontWeight: 600 }}>{kpi.scaffold_suggestion}</span></div>
                            <div style={{ marginTop: 10 }}><i>{kpi.scaffold_explanation}</i></div>
                        </div>
                    </div>}        
                </div>


    </>
  )
}

export default BenchmarkStudentDiagnostic